"use client"

import { useState } from "react"
import { FixedSizeList as List } from "react-window"
import { IoClose, IoSearch, IoPersonAdd, IoCheckmark } from "react-icons/io5"

const AddParticipantsModal = ({ onClose, onAddParticipants, usersData, currentParticipants, isLoading }) => {
  const [participantSearchTerm, setParticipantSearchTerm] = useState("")
  const [selectedParticipants, setSelectedParticipants] = useState(new Set())

  // Filter out users who are already participants
  const availableUsers = usersData.filter(
    (user) => !currentParticipants.some((participant) => participant.userId === user.value),
  )

  // Filter users based on search term
  const filteredParticipants = availableUsers?.filter(
    (user) =>
      user?.label?.toLowerCase()?.includes(participantSearchTerm?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(participantSearchTerm?.toLowerCase()),
  )

  const handleParticipantSelection = (userId) => {
    const newSelectedParticipants = new Set(selectedParticipants)
    if (newSelectedParticipants.has(userId)) {
      newSelectedParticipants.delete(userId)
    } else {
      newSelectedParticipants.add(userId)
    }
    setSelectedParticipants(newSelectedParticipants)
  }

  const handleAdd = () => {
    // Extract userIds of selected participants
    const newParticipantIds = Array.from(selectedParticipants)

    // Pass only the userIds to the parent component
    onAddParticipants(newParticipantIds)
  }

  // Function to generate initials from name
  const getInitials = (name) => {
    const parts = name.split(" ")
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase()
    }
    return name.charAt(0).toUpperCase()
  }

  // Function to generate a consistent color based on user ID
  const getUserColor = (userId) => {
    const colors = [
      "bg-blue-100 text-blue-800",
      "bg-green-100 text-green-800",
      "bg-purple-100 text-purple-800",
      "bg-yellow-100 text-yellow-800",
      "bg-pink-100 text-pink-800",
      "bg-indigo-100 text-indigo-800",
      "bg-red-100 text-red-800",
      "bg-teal-100 text-teal-800",
    ]

    // Convert userId to string and then create a simple hash
    const userIdStr = String(userId)
    // Use a simpler approach to generate a consistent index
    let hash = 0
    for (let i = 0; i < userIdStr.length; i++) {
      hash = (hash + userIdStr.charCodeAt(i)) % colors.length
    }

    return colors[hash]
  }

  const ParticipantRow = ({ index, style }) => {
    const participant = filteredParticipants[index]
    const isSelected = selectedParticipants.has(participant.value)

    return (
      <div
        style={style}
        className={`flex items-center p-3 cursor-pointer transition-colors border-b border-gray-100 ${
          isSelected ? "bg-blue-50" : "hover:bg-gray-50"
        }`}
        onClick={() => handleParticipantSelection(participant.value)}
      >
        <div className="flex items-center w-full">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${getUserColor(participant.value)}`}
          >
            {getInitials(participant.label)}
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{participant.label}</p>
            <p className="text-xs text-gray-500 truncate">{participant.email}</p>
          </div>

          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center ml-2 border ${
              isSelected ? "bg-blue-500 border-blue-500 text-white" : "border-gray-300 bg-white"
            }`}
          >
            {isSelected && <IoCheckmark className="w-4 h-4" />}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-sideNavbg px-6 py-3 flex justify-between items-center">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            {/* <IoPersonAdd className="text-2xl" /> */}
            Add Participants
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-700 rounded-full p-1 transition-colors"
            aria-label="Close"
          >
            <IoClose className="w-6 h-6" />
          </button>
        </div>

        {/* Search input */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IoSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by name or email"
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={participantSearchTerm}
              onChange={(e) => setParticipantSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Participants count */}
        <div className="px-6 py-2 bg-gray-50 border-b">
          <p className="text-sm text-gray-600">
            {filteredParticipants.length} available {filteredParticipants.length === 1 ? "user" : "users"} •{" "}
            {selectedParticipants.size} selected
          </p>
        </div>

        {/* Participants list */}
        <div className="relative">
          {filteredParticipants.length > 0 ? (
            <List
              height={300}
              itemCount={filteredParticipants.length}
              itemSize={64}
              width="100%"
              className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
            >
              {ParticipantRow}
            </List>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <div className="text-gray-400 mb-2">
                <IoSearch className="w-10 h-10 mx-auto" />
              </div>
              <p className="text-gray-600 font-medium">No users found</p>
              <p className="text-gray-500 text-sm mt-1">Try a different search term or add new users to the system</p>
            </div>
          )}
        </div>

        {/* Footer with action buttons */}
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Cancel
          </button>

          <button
            className={`inline-flex items-center px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              !isLoading && selectedParticipants.size > 0
                ? "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
            onClick={handleAdd}
            disabled={isLoading || selectedParticipants.size === 0}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Adding...
              </>
            ) : (
              <>
                <IoPersonAdd className="mr-2" />
                Add {selectedParticipants.size > 0 ? `(${selectedParticipants.size})` : ""}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AddParticipantsModal

